import ActivityList from "@components/ActivityList/ActivityList"
import ChatBubble from "@components/ChatBubble/ChatBubble"
import CollapsingHeader from "@components/CollapsingHeader/CollapsingHeader"
import JagadBeta from "@components/JagadBeta/JagadBeta"
import HomeHeader from "@components/HomeHeader/HomeHeader"
import TabBar from "@components/TabBar/TabBar"
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator"
import { useEffect, useState, useRef, useCallback, memo } from "react"
import {
    Background,
    Container,
    CollapsingHeaderContainer,
    BackgroundContainer,
    RecentActivityContainer,
    RecentActivityText,
    ActivityListContainer,
    Spacer,
    ChatBubbleContainer,
    AlertBoxContainer
} from "./Home.styles"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { redirectToTelegramSupport } from '../../lib/utils'
import { hapticFeedback, setHeaderColor } from '../../lib/telegram'
import AlertBox from "@components/AlertBox/AlertBox"
import { CRIMSON_100, CRIMSON_500, SKY_BLUE, WHITE } from "@styles/colors"
import ReferralJourneyBanner from "@components/ReferralJourneyBanner/ReferralJourneyBanner"
import { useReferralBannerStore } from "@stores/referralBannerStore"
import useSystemHealth, { SystemHealth } from "@hooks/useSystemHealth"
import useReferralJourney from "@hooks/useReferralJourney"
import useBalance from "@hooks/useBalance"
import useActivity from "@hooks/useActivity"
import { ActivityItem } from "../../types/activity"
import useAnnouncement, { Announcement } from "@hooks/useAnnouncement"
import TemporaryTokenMigrationDialog from "@components/TemporaryTokenMigration/TemporaryTokenMigrationDialog/TemporaryTokenMigrationDialog"
// Constants to avoid recreating these values on each render
const COLLAPSING_HEADER_HEIGHT = 60; // in pixels
const TRANSITION_ZONE_HEIGHT = 40;
const SNAP_THRESHOLD = 0.1;
const SCROLL_POSITION_KEY = 'scrollPosition';

// Memoize child components to prevent unnecessary re-renders
const MemoizedActivityList = memo(ActivityList);
const MemoizedTabBar = memo(TabBar);
const MemoizedCollapsingHeader = memo(CollapsingHeader);
const MemoizedReferralJourneyBanner = memo(ReferralJourneyBanner);
const MemoizedAlertBox = memo(AlertBox);

const Home = () => {
    const { data: systemHealth } = useSystemHealth()
    const { data: referralJourney } = useReferralJourney()
    const { data: balance, isLoading: isBalanceLoading, error: balanceError } = useBalance()
    const {
        data: activities,
        isLoading: isActivitiesLoading,
        isLoadingMore,
        loadMore,
        isReachingEnd,
        refetch
    } = useActivity()
    const navigate = useNavigate()
    const { data: announcements } = useAnnouncement(navigate)
    const { t } = useTranslation()
    const { isDismissed } = useReferralBannerStore()
    const [isSnapping, setIsSnapping] = useState(false)
    const [touchStartY, setTouchStartY] = useState<number | null>(null)

    // Use refs for values that don't need to trigger re-renders
    const headerOpacityRef = useRef(0)
    const headerTopRef = useRef(-100)
    const stickyProgressRef = useRef(0)
    const lastScrollTopRef = useRef(0)
    const isScrollingRef = useRef(false)
    const lastHapticTimeRef = useRef(0)

    // Reference to the container element
    const containerRef = useRef<HTMLDivElement>(null)
    // Reference to the background element
    const backgroundRef = useRef<HTMLDivElement>(null)
    // Reference to the recent activity container
    const recentActivityRef = useRef<HTMLDivElement>(null)
    // Reference to the activity list container
    const activityListRef = useRef<HTMLDivElement>(null)
    // Reference to the collapsing header container
    const collapsingHeaderRef = useRef<HTMLDivElement>(null)
    // Store initial position of recent activity container
    const initialPositionRef = useRef<number | null>(null)
    // Timeout for snap animation
    const snapTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    // Store the last animation frame request ID
    const animationFrameRef = useRef<number | null>(null)
    // Reference for the loading indicator element
    const loadingIndicatorRef = useRef<HTMLDivElement>(null)
    // Observer for infinite scrolling
    const observerRef = useRef<IntersectionObserver | null>(null)

    // Update header and background based on scroll position
    const updateHeaderAndBackground = useCallback(() => {
        if (backgroundRef.current && collapsingHeaderRef.current) {
            const backgroundRect = backgroundRef.current.getBoundingClientRect();
            const backgroundHeight = backgroundRect.height;
            const backgroundBottom = backgroundRect.bottom;

            // Calculate how much of the background is out of view (as a percentage)
            // When the bottom of the background reaches the top of the viewport, it's 100% out of view
            let outOfViewPercentage = 0;

            // Prevent division by zero or negative values that could cause NaN
            if (backgroundHeight > 0) {
                outOfViewPercentage = Math.min(1, Math.max(0, 1 - backgroundBottom / backgroundHeight));
            }

            // Ensure we're setting valid numbers for opacity and position
            const safeOpacity = isNaN(outOfViewPercentage) ? 0 : outOfViewPercentage;
            const safeHeaderTop = isNaN(outOfViewPercentage) ? -100 : -100 + (outOfViewPercentage * 100);

            // Update refs instead of state
            headerOpacityRef.current = safeOpacity;
            headerTopRef.current = safeHeaderTop;

            // Apply values directly to DOM elements
            backgroundRef.current.style.setProperty('--header-opacity', safeOpacity.toString());
            collapsingHeaderRef.current.style.setProperty('--header-top', `${safeHeaderTop}px`);
            collapsingHeaderRef.current.style.setProperty('--header-opacity', safeOpacity.toString());
        }
    }, []);

    // Function to scroll to the snap point - memoized to avoid recreation
    const scrollToSnapPoint = useCallback(() => {
        if (containerRef.current && recentActivityRef.current) {
            // Calculate the position to scroll to
            // This should be the position where the RecentActivityContainer is just at the top (minus header height)
            const recentActivityRect = recentActivityRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            // Calculate the target scroll position
            // This is the current scroll position plus the distance from the top of the container
            // to the top of the RecentActivityContainer, minus the header height
            const targetScrollTop = containerRef.current.scrollTop +
                (recentActivityRect.top - containerRect.top) - COLLAPSING_HEADER_HEIGHT;

            // Set snapping flag to prevent scroll events during animation
            setIsSnapping(true);

            // Use smooth scrolling
            containerRef.current.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth'
            });

            // Reset snapping flag after animation completes
            snapTimeoutRef.current = setTimeout(() => {
                setIsSnapping(false);
            }, 500); // Animation takes about 500ms
        }
    }, []);

    // Handle touch start event
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setTouchStartY(e.touches[0].clientY);
    }, []);

    // Handle touch move event
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (touchStartY === null) return;

        const touchY = e.touches[0].clientY;
        const deltaY = touchStartY - touchY;

        // Check if we're at the top of the container
        const isAtTop = containerRef.current && containerRef.current.scrollTop <= 0;

        // If user is trying to scroll down (dragging finger up)
        if (deltaY > 0) {
            // Allow normal scrolling
        }
        // If user is trying to scroll up (dragging finger down)
        else if (deltaY < 0) {
            // Check if we're at the top of the container
            if (isAtTop) {
                // If pull distance exceeds threshold, trigger haptic feedback
                if (Math.abs(deltaY) > 300) {
                    // Get current time
                    const now = Date.now();

                    // Only trigger haptic feedback if it's been at least 1 second since the last trigger
                    if (now - lastHapticTimeRef.current > 1000) {
                        // Trigger haptic feedback
                        hapticFeedback('heavy');
                        refetch()

                        // Update the last haptic time
                        lastHapticTimeRef.current = now;
                    }
                }
            }

            // Force update the header and background when scrolling down
            // This ensures the background reappears properly
            updateHeaderAndBackground();
        }
    }, [touchStartY, updateHeaderAndBackground]);

    // Handle touch end event
    const handleTouchEnd = useCallback(() => {
        setTouchStartY(null);
    }, []);

    // Optimized scroll handler using requestAnimationFrame for better performance
    const handleScroll = useCallback(() => {
        // Skip scroll handling during snap animation or if already scrolling
        if (isSnapping || isScrollingRef.current) return;

        // Set scrolling flag to prevent multiple handlers
        isScrollingRef.current = true;

        // Use requestAnimationFrame to throttle scroll events
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = requestAnimationFrame(() => {
            if (!containerRef.current || !recentActivityRef.current) return;

            const currentScrollTop = containerRef.current.scrollTop;

            const scrollingDown = currentScrollTop > lastScrollTopRef.current;
            lastScrollTopRef.current = currentScrollTop;

            // Always update header and background on scroll
            updateHeaderAndBackground();

            // Handle Recent Activity container smooth sticky transition
            if (initialPositionRef.current !== null) {
                const recentActivityRect = recentActivityRef.current.getBoundingClientRect();
                const currentTop = recentActivityRect.top;

                // Calculate the sticky transition progress
                const transitionStartPoint = COLLAPSING_HEADER_HEIGHT + TRANSITION_ZONE_HEIGHT;

                if (currentTop <= transitionStartPoint) {
                    // Calculate progress (0 to 1) based on position within transition zone
                    const progress = Math.min(1, Math.max(0,
                        (transitionStartPoint - currentTop) / TRANSITION_ZONE_HEIGHT
                    ));

                    // Update ref instead of state
                    stickyProgressRef.current = progress;

                    // Apply value directly to DOM element
                    if (recentActivityRef.current) {
                        recentActivityRef.current.style.setProperty('--sticky-progress', progress.toString());
                    }

                    // Apply snap behavior when scrolling down, but not when scrolling up
                    if (scrollingDown && progress >= SNAP_THRESHOLD && progress < 1) {
                        scrollToSnapPoint();
                    }
                } else {
                    stickyProgressRef.current = 0;
                    if (recentActivityRef.current) {
                        recentActivityRef.current.style.setProperty('--sticky-progress', '0');
                    }
                }
            }

            // Reset scrolling flag
            isScrollingRef.current = false;
        });
    }, [isSnapping, updateHeaderAndBackground, scrollToSnapPoint]);

    // Set up intersection observer for infinite scrolling
    useEffect(() => {
        // Create the intersection observer
        observerRef.current = new IntersectionObserver(
            (entries) => {
                // If the loading indicator is visible and we're not already loading more or at the end
                if (entries[0].isIntersecting && !isLoadingMore && !isReachingEnd) {
                    loadMore();
                }
            },
            { threshold: 0.1 } // Trigger when 10% of the element is visible
        );

        // Start observing the loading indicator element
        if (loadingIndicatorRef.current) {
            observerRef.current.observe(loadingIndicatorRef.current);
        }

        return () => {
            // Clean up the observer when component unmounts
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [isLoadingMore, isReachingEnd, loadMore]);

    useEffect(() => {
        // Capture initial position of recent activity container after initial render
        if (recentActivityRef.current) {
            initialPositionRef.current = recentActivityRef.current.getBoundingClientRect().top;
        }

        // Add a resize listener to recapture initial position if window size changes
        const handleResize = () => {
            if (recentActivityRef.current) {
                initialPositionRef.current = recentActivityRef.current.getBoundingClientRect().top;
            }
            // Also update header and background on resize
            updateHeaderAndBackground();
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            // Clear any pending snap timeouts
            if (snapTimeoutRef.current) {
                clearTimeout(snapTimeoutRef.current);
            }
            // Cancel any pending animation frames
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [updateHeaderAndBackground]);

    // Set initial scroll position when component mounts
    useEffect(() => {
        if (!containerRef.current) return;

        // Set an initial scroll position (e.g., 200px from the top)
        const initialScrollPosition = Number(sessionStorage.getItem(SCROLL_POSITION_KEY)) || 0;


        // Use a small timeout to ensure the component is fully rendered
        setTimeout(() => {
            if (containerRef.current) {
                // Direct assignment of scrollTop
                containerRef.current.scrollTop = initialScrollPosition;

                // Force update header and background after setting scroll
                updateHeaderAndBackground();
                sessionStorage.removeItem(SCROLL_POSITION_KEY);
            }
        }, 100);
    }, [updateHeaderAndBackground]);

    useEffect(() => {
        setHeaderColor(SKY_BLUE)
    }, [])

    return (
        <Container
            className="hide-scrollbar"
            ref={containerRef}
            onScroll={handleScroll}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <TemporaryTokenMigrationDialog
                balance={balance}
                isBalanceLoading={isBalanceLoading}
                balanceError={balanceError}
            />
            <Background
                ref={backgroundRef}
                as={BackgroundContainer}
                style={{ '--header-opacity': '0' } as React.CSSProperties}
            >
                <JagadBeta />
                <HomeHeader
                    balance={balance?.confirmed_balance ?? 'unavailable'}
                    isBalanceLoading={isBalanceLoading}
                />
            </Background>
            <CollapsingHeaderContainer
                ref={collapsingHeaderRef}
                style={{
                    '--header-top': '-100px',
                    '--header-opacity': '0'
                } as React.CSSProperties}
            >
                <MemoizedCollapsingHeader balance={balance?.confirmed_balance ?? 0} />
            </CollapsingHeaderContainer>

            <AlertBoxContainer>
                {systemHealth &&
                    systemHealth.map((item: SystemHealth, index: number) => (
                        <MemoizedAlertBox
                            key={index}
                            alertText={item.warning_text}
                            backgroundColor={CRIMSON_100}
                            borderColor={CRIMSON_100}
                            iconFillColor={CRIMSON_500}
                            iconOutlineColor={CRIMSON_500}
                            iconExclamationColor={WHITE}
                            fontSize="13px"
                        />
                    ))
                }
                {announcements &&
                    announcements.map((item: Announcement) => (
                        <MemoizedAlertBox
                            key={item.uuid}
                            alertText={item.announcement_text}
                            backgroundColor={item.secondary_color}
                            borderColor={item.primary_color}
                            iconFillColor={item.primary_color}
                            iconOutlineColor={item.primary_color}
                            iconExclamationColor={item.secondary_color}
                            fontSize="13px"
                            actionText={item.button_text}
                            onClick={item.action}
                        />
                    ))
                }
                {/* Don't fetch referral info when user opt out */}
                {/* Banner will only show if not dismissed in the store */}
                {
                    referralJourney &&
                    referralJourney.referral_eligibility &&
                    !isDismissed &&
                    <MemoizedReferralJourneyBanner
                        refereeInfo={referralJourney}
                        animationDuration={0.5}
                    />
                }
            </AlertBoxContainer>

            <RecentActivityContainer
                ref={recentActivityRef}
                style={{ '--sticky-progress': '0' } as React.CSSProperties}
                $collapsingHeaderHeight={COLLAPSING_HEADER_HEIGHT}
            >
                <RecentActivityText>{t('page.home.recentActivity')}</RecentActivityText>
            </RecentActivityContainer>
            <ActivityListContainer ref={activityListRef}>
                <MemoizedActivityList
                    activities={activities || []}
                    isLoading={!!isActivitiesLoading}
                    onItemClick={useCallback((activity: ActivityItem) => {
                        sessionStorage.setItem(SCROLL_POSITION_KEY, containerRef.current?.scrollTop.toString() || '0');
                        navigate('/transaction-detail/', {
                            state: {
                                ...activity,
                                from: '/home',
                            }
                        })
                    }, [navigate])}
                />
                {/* Loading indicator for infinite scrolling */}
                <div ref={loadingIndicatorRef}>
                    <ActivityIndicator visible={isLoadingMore} size={24} />
                </div>
            </ActivityListContainer>
            {/* Spacer to ensure scrollability even with minimal content */}
            <Spacer />
            <ChatBubbleContainer>
                <ChatBubble onClick={() => redirectToTelegramSupport('JagadCS_bot')} />
            </ChatBubbleContainer>
            <MemoizedTabBar navigate={navigate} />
        </Container>
    )
}

export default Home